/* eslint-disable @typescript-eslint/no-explicit-any */
import PasswordInput from "~/common/components/materialInput/PasswordInput";
import { Controller, type FieldError, type UseFormReturn } from "react-hook-form";
import { PASSWORD_REGEXP } from "~/common/utils/validations";
import { FormSection } from "~/pages/MyAccountPage/MyAccountPage";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";

interface Props {
  control: UseFormReturn<any>["control"];
  values: {
    oldPassword: string;
    newPassword: string;
    newPasswordConfirmation: string;
  };
  triggerValidation: (payload?: any) => Promise<boolean>;
  className?: string;
}

export const ChangePasswordSection = (props: Props) => {
  const { t } = useTranslation();
  const { control, values, triggerValidation } = props;

  const tryingToChangePassword = !(
    values.oldPassword === "" &&
    values.newPassword === "" &&
    values.newPasswordConfirmation === ""
  );

  const getHelperLabelForNewPassword = (fieldsError: FieldError | undefined) => {
    const DoNotaMatchReq = t("page.completeRegistration.errorMatchRequirements");
    const TooShort = t("myAccount.password.error.tooShort");
    const PasswordsDoNotMatch = t("page.completeRegistration.errorTwoPasswordsMatch");
    if (fieldsError) {
      if (fieldsError.type === "minLength") {
        return TooShort;
      } else if (fieldsError.type === "pattern" || fieldsError.type === "maxLength") {
        return DoNotaMatchReq;
      } else if (fieldsError.type === "validate") {
        return PasswordsDoNotMatch;
      }
    } else {
      return "";
    }
  };

  return (
    <FormSection
      className={props.className}
      title={t("myAccount.password.title")}
      data-test-id={"change-password-block"}
    >
      <Controller
        name="oldPassword"
        control={control}
        rules={{ required: tryingToChangePassword }}
        render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
          <PasswordInput
            label={t("myAccount.oldPassword.label") + "*"}
            data-test-id={"old-password"}
            helperLabel={error ? t("myAccount.oldPassword.error") : true}
            error={!!error}
            onBlur={() => {
              onBlur();
              triggerValidation("oldPassword");
            }}
            inputRef={ref}
            {...inputProps}
          />
        )}
      />

      <Controller
        name="newPassword"
        control={control}
        rules={{
          pattern: PASSWORD_REGEXP,
          minLength: 8,
          maxLength: 255,
          required: tryingToChangePassword,
        }}
        render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
          <PasswordInput
            label={t("myAccount.newPassword.label") + "*"}
            data-test-id={"new-password"}
            helperLabel={error ? getHelperLabelForNewPassword(error) : true}
            error={!!error}
            onBlur={() => {
              onBlur();
              triggerValidation("newPassword");
            }}
            inputRef={ref}
            {...inputProps}
          />
        )}
      />

      <Controller
        name="newPasswordConfirmation"
        control={control}
        rules={{
          validate: (value) => value === values.newPassword,
          required: tryingToChangePassword,
        }}
        render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
          <PasswordInput
            label={t("myAccount.newPasswordConfirm.label") + "*"}
            data-test-id={"new-confirm-password"}
            helperLabel={error ? getHelperLabelForNewPassword(error) : true}
            error={!!error}
            onBlur={() => {
              onBlur();
              triggerValidation("newPasswordConfirmation");
            }}
            inputRef={ref}
            {...inputProps}
          />
        )}
      />

      <Alert className="mt-30" severity={"info"}>
        {t("page.completeRegistration.passwordInfo")}
      </Alert>
    </FormSection>
  );
};
